// ── Calendar helpers: bounded, inclusive date-range presentation ───────────
// The page requests the handwritten `/calendar` read with an inclusive from/to
// window. The API applies scope and date predicates; these helpers only bucket
// the returned sessions and format their existing local wall times.

import type { CoachingSessionDto } from "../../services/coaching-dtos";

export interface CalendarDateRange {
  [key: string]: string;
  from: string;
  to: string;
}

export function currentMonthDateRange(now: Date = new Date()): CalendarDateRange {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const pad = (value: number) => String(value).padStart(2, "0");
  const from = `${year}-${pad(month + 1)}-01`;
  const to = `${year}-${pad(month + 1)}-${pad(new Date(Date.UTC(year, month + 1, 0)).getUTCDate())}`;
  return { from, to };
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The range the calendar asks for (F20): `from`/`to` from the URL when both are
 * given, passed through exactly as written (the API refuses a malformed or
 * inverted range and applies role scope); otherwise the current UTC month.
 */
export function calendarRangeFromSearch(search: URLSearchParams, now: Date = new Date()): CalendarDateRange {
  const from = search.get("from") ?? "";
  const to = search.get("to") ?? "";
  return from && to ? { from, to } : currentMonthDateRange(now);
}

/** The whole month `offset` months from the month `range.from` is in; null when `from` isn't a date. */
export function shiftedMonthDateRange(range: CalendarDateRange, offset: number): CalendarDateRange | null {
  if (!DATE_ONLY.test(range.from)) return null;
  const year = Number(range.from.slice(0, 4));
  const month = Number(range.from.slice(5, 7)) - 1;
  return currentMonthDateRange(new Date(Date.UTC(year, month + offset, 1)));
}

/**
 * The calendar URL for `range` (null: the current month). Every other query
 * parameter on the page, such as the evidence `?role=`, is kept.
 */
export function calendarRangeHref(search: URLSearchParams, range: CalendarDateRange | null): string {
  const params = new URLSearchParams(search);
  if (range) {
    params.set("from", range.from);
    params.set("to", range.to);
  } else {
    params.delete("from");
    params.delete("to");
  }
  const query = params.toString();
  return query ? `/calendar?${query}` : "/calendar";
}

/** "1 Nov 2026 – 30 Nov 2026" for real dates; anything else is shown as given. */
export function describeDateRange(range: CalendarDateRange): string {
  const format = (value: string) => {
    if (!DATE_ONLY.test(value)) return value;
    const date = new Date(`${value}T12:00:00Z`);
    if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) return value;
    return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(date);
  };
  return `${format(range.from)} – ${format(range.to)}`;
}

export interface CalendarDayBucket {
  /** YYYY-MM-DD key, or "undated" for sessions without a parseable start. */
  dateKey: string;
  /** Display label (e.g. "Sun, Mar 8") or "No scheduled date". */
  label: string;
  sessions: CoachingSessionDto[];
}

/** Parse the YYYY-MM-DD portion of a session start (ISO or date-only). */
export function sessionDateKey(session: CoachingSessionDto): string | null {
  const raw = (session.starts_at ?? "").trim();
  if (!raw) return null;
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
}

function labelForDateKey(dateKey: string): string {
  if (dateKey === "undated") return "No scheduled date";
  // Construct at UTC noon to avoid timezone roll-back on the date-only key.
  const date = new Date(`${dateKey}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return new Intl.DateTimeFormat("en", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC"
  }).format(date);
}

/**
 * Group sessions into day buckets, sorted ascending by date (undated last). The
 * input is the role-scoped list verbatim; this is pure presentation bucketing.
 */
export function bucketSessionsByDay(sessions: CoachingSessionDto[]): CalendarDayBucket[] {
  const buckets = new Map<string, CoachingSessionDto[]>();
  for (const session of sessions) {
    const key = sessionDateKey(session) ?? "undated";
    const list = buckets.get(key) ?? [];
    list.push(session);
    buckets.set(key, list);
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => {
      if (a === "undated") return 1;
      if (b === "undated") return -1;
      return a.localeCompare(b);
    })
    .map(([dateKey, daySessions]) => ({
      dateKey,
      label: labelForDateKey(dateKey),
      sessions: daySessions.slice().sort(sortByStart)
    }));
}

function sortByStart(a: CoachingSessionDto, b: CoachingSessionDto): number {
  return (a.starts_at ?? "").localeCompare(b.starts_at ?? "");
}

/** Time-of-day label (e.g. "14:00") from an ISO start, honest "—" when absent. */
export function sessionTimeLabel(session: CoachingSessionDto): string {
  const raw = (session.starts_at ?? "").trim();
  const match = raw.match(/T(\d{2}:\d{2})/);
  return match ? match[1] : "—";
}
